import { useCallback, useEffect, useState } from "react";

import { getReadOnlyContract, getReadOnlyProvider } from "../services/contract.service";
import {
  ChallengeKind,
  ChallengeRecord,
  ChallengeState,
  ReviewWindow,
  isLiveChallengeState,
  listChallengesFor,
  readWindow,
} from "../services/challenge.service";

// The V10.1 review state of a minted covenant, read from chain in one round trip.
//
// The backend mirror (GET /settlement/:tokenId → termChallenge) still keys off EcoBank's V9 events
// and knows nothing about EcoChallenge's windows, halts or seat transfers, so everything a
// guardian or patron needs to understand WHY a release stopped has to come from here:
//
//   frozen             EcoBank.dripFrozen — an inBreach finding froze the TREE drip. Cured only by a
//                      real re-verification followed by releaseAfterReVerification (§3).
//   releasesHalted     EcoChallenge.releasesHalted — EcoBank staticcalls this and reverts
//                      ReleasesHalted. Two independent causes, both exposed so the copy can say which:
//   unattestedRuns       ≥ unattestedHaltAt (3) consecutive review windows nobody acted on (§5), or
//   awaitingBetterRecord an upheld EVIDENCE finding with no better record filed since (§0 / C6).
//   verificationsAtFreeze / verificationsPerformed
//                      the proof anchor: releaseAfterReVerification requires performed > atFreeze,
//                      so `reVerified` is exactly the condition under which the release call will
//                      succeed rather than revert NotReVerified.
//   window             windowOf(tokenId) — the open (or last) review window, for the attest button
//                      and its countdown.
//   challenge          the live (OPEN / RESPONDED / SEATED) challenge against the token, if any.
//   lastDetermined     the most recent DETERMINED one — the id releaseAfterReVerification needs,
//                      which nothing on the token points at once a fresh window has opened.
//
// Nothing here writes; every read is a view on the read-only provider, so mounting the card never
// prompts a wallet.

export interface CovenantReviewState {
  frozen: boolean;
  frozenAt: Date | null;
  releasesHalted: boolean;
  unattestedRuns: number;
  unattestedHaltAt: number;
  awaitingBetterRecord: boolean;
  verificationsAtFreeze: number;
  verificationsPerformed: number;
  // performed > atFreeze — releaseAfterReVerification would succeed.
  reVerified: boolean;
  // The covenant's seated verifier, as Core holds it (moves on a reseat).
  verifier: string;
  // The covenant's guardian. With the verifier, the two wallets _requireIndependent refuses
  // outright, so the card never offers them attest / challenge.
  guardian: string;
  // The Trust Admin who admitted that verifier — the one Core lets reseat it (and a GTA).
  sponsoringHolder: string;
  window: ReviewWindow | null;
  challenge: ChallengeRecord | null;
  lastDetermined: ChallengeRecord | null;
  // An upheld FIELD finding moved the seat (SeatTransferRequired, EcoChallenge.sol:805-812) and
  // the covenant's verifier is STILL the dismissed one: the Holder has dismiss → reseat ahead of
  // them, in that order (Core.addVerifierToEasement refuses until the seated verifier is
  // dismissed or overdue, EcoFuturesCore.sol:561-565). Clears on its own once the reseat lands,
  // because `verifier` then differs from the finding's — nothing has to record it.
  reseatRequired: boolean;
  // The last determined challenge upheld a DEED-stage challenge that is still holding the
  // settlement — released by releaseAfterReVerification once re-verified, never refunded
  // (EcoChallenge.sol:962-984). The release fallback needs this as much as the freeze.
  heldSettlement: boolean;
  // Chain time minus wall-clock time at the read, in ms. Window checks compare `closesAt` with
  // the chain's clock (the contract's block.timestamp), not the viewer's — a skewed laptop clock
  // would otherwise offer a challenge the chain calls late. Add it to Date.now().
  chainClockOffsetMs: number;
}

const toDate = (v: bigint | number): Date | null => (Number(v) > 0 ? new Date(Number(v) * 1000) : null);

export const fetchCovenantReviewState = async (tokenId: bigint | number | string): Promise<CovenantReviewState> => {
  const bank = getReadOnlyContract("ecofutures-bank");
  const challenge = getReadOnlyContract("challenge");
  const core = getReadOnlyContract("ecofutures-core");
  if (!bank || !challenge || !core) {
    throw new Error("Bank, EcoChallenge or Core contract not found");
  }
  const id = BigInt(tokenId);
  const [
    frozen,
    frozenAt,
    releasesHalted,
    unattestedRuns,
    unattestedHaltAt,
    awaitingBetterRecord,
    verificationsAtFreeze,
    easement,
    window,
    challenges,
    latestBlock,
  ] = await Promise.all([
    bank.dripFrozen(id),
    bank.dripFrozenAt(id),
    challenge.releasesHalted(id),
    challenge.unattestedRuns(id),
    challenge.unattestedHaltAt(),
    challenge.awaitingBetterRecord(id),
    challenge.verificationsAtFreeze(id),
    core.getEasement(id),
    readWindow(id),
    // One capped scan (newest first) serves both the live challenge and the last determined one;
    // a failed scan is an empty list, not an error — the card renders without it.
    listChallengesFor(id, 60).catch(() => [] as ChallengeRecord[]),
    // Only for the clock offset; a failed read falls back to the wall clock.
    getReadOnlyProvider()
      .getBlock("latest")
      .catch(() => null),
  ]);
  const atFreeze = Number(verificationsAtFreeze);
  const performed = Number(easement.verificationsPerformed);
  const verifier = String(easement.verifier).toLowerCase();
  const lastDetermined = challenges.find((c) => c.state === ChallengeState.DETERMINED) ?? null;
  return {
    frozen: Boolean(frozen),
    frozenAt: toDate(frozenAt),
    releasesHalted: Boolean(releasesHalted),
    unattestedRuns: Number(unattestedRuns),
    unattestedHaltAt: Number(unattestedHaltAt),
    awaitingBetterRecord: Boolean(awaitingBetterRecord),
    verificationsAtFreeze: atFreeze,
    verificationsPerformed: performed,
    reVerified: performed > atFreeze,
    verifier,
    guardian: String(easement.guardian).toLowerCase(),
    sponsoringHolder: String(easement.sponsoringHolder).toLowerCase(),
    window,
    challenge: challenges.find((c) => isLiveChallengeState(c.state)) ?? null,
    lastDetermined,
    reseatRequired:
      !!lastDetermined &&
      lastDetermined.upheld &&
      lastDetermined.kind === ChallengeKind.FIELD &&
      lastDetermined.verifier.toLowerCase() === verifier,
    heldSettlement: !!lastDetermined && lastDetermined.upheld && lastDetermined.heldSettlement,
    chainClockOffsetMs: latestBlock ? latestBlock.timestamp * 1000 - Date.now() : 0,
  };
};

// Hook form. `state` stays at the last good read on a failed refresh rather than blanking the
// card mid-action; `error` carries the reason. Call refresh() after any on-chain action lands.
const useCovenantReviewState = (tokenId: bigint | number | string | null | undefined) => {
  const [state, setState] = useState<CovenantReviewState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (tokenId === null || tokenId === undefined || tokenId === "") {
      setState(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setState(await fetchCovenantReviewState(tokenId));
    } catch (e: any) {
      setError(e?.shortMessage || e?.message || "Could not read the covenant's review state");
    } finally {
      setLoading(false);
    }
  }, [tokenId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { state, loading, error, refresh };
};

export default useCovenantReviewState;
